import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react"
import { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions"
import {
  RootState,
  dynamicMiddlewaresInstance,
  registerReducer,
} from "@core/store"
import { baseQueryWithReauth } from "@/modules/app/store"
import { TG, TgMailing } from "../interfaces"
import { BotMonitorRequest, BotMonitorResponse } from "../interfaces/botMonitor"
import { botMonitorRequestParams } from "../utils/botMonitorQuery"
import {
  BOT_MONITOR_INVALID_RESPONSE,
  isBotMonitorResponse,
} from "../utils/botMonitorResponse"

const tagTypes = [
  "Updates",
  "NcMembers",
  "ActiveMailings",
  "FinishedMailings",
  "MailingTypes",
] as const

const reducerPath: string = "telegramApi"
export const baseTGStatApiURL = new URL(
  import.meta.env.REACT_APP_TGSTAT_API,
  window.location.origin,
).toString()
export const baseUrl = `${
  baseTGStatApiURL.endsWith("/") ? baseTGStatApiURL : baseTGStatApiURL + "/"
}telegram`
const baseQuery: BaseQueryFn = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token
    if (token) {
      headers.set("authorization", `Bearer ${token}`)
    }
    return headers
  },
})

type ApiEndpointBuider = EndpointBuilder<
  typeof baseQuery,
  (typeof tagTypes)[number],
  typeof reducerPath
>

const endpoints = (build: ApiEndpointBuider) => ({
  botMonitorBots: build.query<BotMonitorResponse, BotMonitorRequest>({
    async queryFn({ queryParams }, _api, _extraOptions, configuredBaseQuery) {
      const result = await configuredBaseQuery({
        url: "bot-monitor/bots",
        method: "GET",
        params: botMonitorRequestParams(queryParams),
      })
      if (result.error) return { error: result.error }
      if (!isBotMonitorResponse(result.data)) {
        return {
          error: {
            status: "CUSTOM_ERROR",
            error: BOT_MONITOR_INVALID_RESPONSE,
          },
        }
      }
      return { data: result.data }
    },
  }),
  findUpdates: build.query<TG.FindUpdatesRes, TG.FindUpdatesReq>({
    query: ({ queryParams }) => ({
      url: `find-text`,
      method: "GET",
      params: {
        take: 100,
        skip: 0,
        ...queryParams,
      },
    }),
    providesTags: ["Updates"],
  }),
  findNcMembers: build.query<TG.FindNcMembersRes, TG.FindNcMembersReq>({
    query: ({ queryParams: { take, skip, find, groupByDate } }) => ({
      url: `find-nc-member`,
      method: "GET",
      params: {
        take: take ?? 100,
        skip: skip ?? 0,
        find: JSON.stringify(find),
        groupByDate,
      },
    }),
    providesTags: ["NcMembers"],
  }),
  findNcUpdates: build.query<TG.FindNcMessagesRes, TG.FindNcMessagesReq>({
    query: ({ queryParams: { take, skip, find } }) => ({
      url: `find-updates`,
      method: "GET",
      params: {
        take: take ?? 100,
        skip: skip ?? 0,
        find: JSON.stringify(find),
      },
    }),
    providesTags: ["NcMembers"],
  }),
  createMailingInit: build.mutation<
    TgMailing.CreateMailingInitRes,
    TgMailing.CreateMailingInitReq
  >({
    query: ({ body }) => ({
      url: `mailing/init`,
      method: "POST",
      body,
    }),
    invalidatesTags: ["ActiveMailings"],
  }),
  // Страница отправляется заранее сериализованной строкой: RTK не пересериализует
  // строку, байтовые ретраи гарантированы. Без заголовка строка ушла бы text/plain.
  sendRecipientsPage: build.mutation<
    TgMailing.SendRecipientsPageRes,
    TgMailing.SendRecipientsPageArgs
  >({
    query: ({ pathParams, bodyJson }) => ({
      url: `mailing/${pathParams.id}/recipients`,
      method: "POST",
      body: bodyJson,
      headers: { "Content-Type": "application/json" },
      // timeout читается fetchBaseQuery из FetchArgs (arg), НЕ из extraOptions:
      // в RTK 1.9.7 extraOptions.timeout молча игнорируется
      timeout: 60_000,
    }),
  }),
  commitMailing: build.mutation<
    TgMailing.CommitMailingRes,
    TgMailing.CommitMailingReq
  >({
    query: ({ pathParams, body }) => ({
      url: `mailing/${pathParams.id}/commit`,
      method: "POST",
      body,
      timeout: 60_000, // FetchArgs.timeout — см. примечание к sendRecipientsPage
    }),
    invalidatesTags: ["ActiveMailings"],
  }),
  enqueueProgress: build.query<
    TgMailing.EnqueueProgressRes,
    TgMailing.EnqueueProgressReq
  >({
    query: ({ pathParams }) => ({
      url: `mailing/${pathParams.id}/enqueue-progress`,
      method: "GET",
    }),
  }),
  activeMailings: build.query<
    TgMailing.ActiveMailingsRes,
    TgMailing.ActiveMailingsReq
  >({
    query: () => ({
      url: `active-mailings`,
      method: "GET",
    }),
    providesTags: ["ActiveMailings"],
  }),
  finishedMailings: build.query<
    TgMailing.FinishedMailingsRes,
    TgMailing.FinishedMailingsReq
  >({
    query: () => ({
      url: `finished-mailings`,
      method: "GET",
    }),
    providesTags: ["FinishedMailings"],
  }),
  mailingTypes: build.query<
    TgMailing.MailingTypesRes,
    TgMailing.MailingTypesReq
  >({
    query: () => ({
      url: `mailing-types`,
      method: "GET",
    }),
    providesTags: ["MailingTypes"],
  }),
  // Полная отмена: останавливает рассылку и удаляет все данные без отчёта.
  removeMailing: build.mutation<
    TgMailing.RemoveMailingRes,
    TgMailing.RemoveMailingReq
  >({
    query: ({ queryParams }) => ({
      url: `remove`,
      method: "DELETE",
      params: { id: queryParams.id },
    }),
    invalidatesTags: ["ActiveMailings", "FinishedMailings"],
  }),
  // Досрочное завершение: останавливает рассылку и сохраняет отчёт в БД.
  finishMailing: build.mutation<
    TgMailing.FinishMailingRes,
    TgMailing.FinishMailingReq
  >({
    query: ({ queryParams }) => ({
      url: `finnish`,
      method: "PATCH",
      params: { id: queryParams.id },
    }),
    invalidatesTags: ["ActiveMailings", "FinishedMailings"],
  }),
})

export const telegramApi = createApi({
  reducerPath,
  tagTypes,
  baseQuery: baseQueryWithReauth(baseQuery),
  endpoints: (build) => ({
    ...endpoints(build),
  }),
})
dynamicMiddlewaresInstance.addMiddleware(telegramApi.middleware)
registerReducer(telegramApi.reducerPath, telegramApi.reducer)

export const {
  useBotMonitorBotsQuery,
  useFindUpdatesQuery,
  useFindNcMembersQuery,
  useFindNcUpdatesQuery,
  useCreateMailingInitMutation,
  useCommitMailingMutation,
  useActiveMailingsQuery,
  useFinishedMailingsQuery,
  useMailingTypesQuery,
  useRemoveMailingMutation,
  useFinishMailingMutation,
} = telegramApi
